import axios from "axios";

import appConfig from "../config/appConfig";

const API_BASE_URL = `${appConfig.apiBase}/prescriptions`;
const MAX_PRESCRIPTION_UPLOAD_SIZE = 50 * 1024 * 1024; // 50 MB

export const uploadPrescription = async (fileBlob, appointmentData, clinicId) => {

    try {

        /* ===============================
            VALIDATE FILE
        =============================== */

        if (!fileBlob) {
            throw new Error("Invalid prescription file generated");
        }

        if (!(fileBlob instanceof Blob)) {
            throw new Error("Invalid prescription file generated");
        }

        if (fileBlob.size === 0) {
            throw new Error("Generated prescription file is empty");
        }

        if (fileBlob.size > MAX_PRESCRIPTION_UPLOAD_SIZE) {
            throw new Error("Prescription file is too large. Please keep the file under 50MB.");
        }

        console.debug("Uploading prescription file", {
            name: fileBlob.name,
            type: fileBlob.type,
            size: fileBlob.size,
            mode: appointmentData.mode
        });

        const appointmentId = Number(appointmentData.appointmentId);
        const patientId = Number(appointmentData.patientId);
        const doctorId = Number(appointmentData.doctorId);

        if (!Number.isFinite(appointmentId) || appointmentId <= 0) {
            throw new Error("Unable to save prescription: invalid appointment reference.");
        }

        if (!Number.isFinite(patientId) || patientId <= 0) {
            throw new Error("Unable to save prescription: invalid patient reference.");
        }

        /* ===============================
            FILE NAME
        =============================== */

        const extension = fileBlob.name?.split(".").pop() || "pdf";
        const filename = `prescription_${appointmentId}_${Date.now()}.${extension}`;

        const formData = new FormData();
        formData.append("file", fileBlob, filename);
        formData.append("appointmentId", String(appointmentId));
        formData.append("patientId", String(patientId));
        if (Number.isFinite(doctorId) && doctorId > 0) {
            formData.append("doctorId", String(doctorId));
        }
        if (appointmentData.prescriptionCode) {
            formData.append("prescriptionCode", appointmentData.prescriptionCode);
        }
        formData.append(
            "prescriptionType",
            appointmentData.mode === "DIGITAL" ? "WRITE" : "UPLOAD"
        );
        formData.append("diagnosis", appointmentData.diagnosis || "General Consultation");
        formData.append("medicines", JSON.stringify(appointmentData.medicines || []));
        formData.append("notes", appointmentData.notes || "");
        if (appointmentData.nextFollowUpDate) {
            formData.append("nextFollowUpDate", appointmentData.nextFollowUpDate);
        }

        const response = await axios.post(
            `${API_BASE_URL}/upload-save`,
            formData,
            {
                headers: {
                    "X-Clinic-Id": clinicId
                }
            }
        );

        return response.data;

    } catch (apiError) {

        console.error(
            "Upload Error Details:",
            apiError.response?.data || apiError.message
        );

        if (apiError.response?.status === 413) {
            throw new Error("Prescription upload failed: file size exceeds the allowed limit.");
        }

        throw new Error(
            apiError.response?.data?.message ||
            apiError.message ||
            "Prescription upload failed"
        );
    }
};
